import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import CONSTANTS from "../utils/constants";

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "46px",
    borderRadius: "0.5rem",
    borderColor: state.isFocused ? "#e03e3e" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(224, 62, 62, 0.25)" : null,
    "&:hover": { borderColor: "#e03e3e" },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#e03e3e"
      : state.isFocused
      ? "#fff5f0"
      : null,
    color: state.isSelected ? "white" : "#212529",
    "&:active": { backgroundColor: "#e03e3e" },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "0.5rem",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  }),
};

export default function AdminOrderPrice() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("prices");
  const [locations, setLocations] = useState([]);
  const [locationPrices, setLocationPrices] = useState([]);
  const [newLocation, setNewLocation] = useState({
    region_id: "",
    city_id: "",
    price: "",
    express_price: "",
  });
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [modalLocationData, setModalLocationData] = useState({
    region_id: "",
    city_id: "",
    price: "",
    express_price: "",
  });
  const [newLocationData, setNewLocationData] = useState({
    region: "",
    region_en: "",
    region_ru: "",
    etrap: "",
    etrap_en: "",
    etrap_ru: "",
  });
  const [editingLocationData, setEditingLocationData] = useState({
    id: "",
    region: "",
    region_en: "",
    region_ru: "",
    etrap: "",
    etrap_en: "",
    etrap_ru: "",
  });
  const [userPermissions, setUserPermissions] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = Cookies.get("user") ? JSON.parse(Cookies.get("user")) : null;
        if (!user?.id) return navigate("/admin");

        const { data: { permissions } } = await axios.get(
          `${CONSTANTS.baseRoute}/permissions`,
          { params: { user_id: user.id } }
        );
        setUserPermissions(permissions);

        if (!permissions.includes(23)) {
          setMessage({ type: "danger", text: t("orderPrice.noPermissionView") });
          setTimeout(() => navigate("/admin/home"), 2500);
          return;
        }

        await Promise.all([fetchLocations(), fetchLocationPrices()]);
      } catch (error) {
        console.error("Data fetch error:", error);
        setMessage({ type: "danger", text: t("orderPrice.fetchError") });
        if ([401, 403].includes(error.response?.status)) navigate("/admin");
      }
    };

    fetchData();
  }, [navigate, t]);

  const fetchLocations = async () => {
    try {
      const { data } = await axios.get(`${CONSTANTS.baseRoute}/locations`);
      setLocations(data);
    } catch (err) {
      console.error(err);
      setMessage({ type: "danger", text: t("orderPrice.locationsFetchError") });
    }
  };

  const fetchLocationPrices = async () => {
    try {
      const { data } = await axios.get(`${CONSTANTS.baseRoute}/locations-price`);
      setLocationPrices(data);
    } catch (err) {
      console.error(err);
      setMessage({ type: "danger", text: t("orderPrice.pricesFetchError") });
    }
  };

  const hasPermission = (permId) => userPermissions.includes(permId);

  const showNotification = (type, text, duration = 3500) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), duration);
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!hasPermission(24)) return showNotification("danger", t("orderPrice.noPermissionAdd"));
    if (!newLocation.region_id || !newLocation.city_id || !newLocation.price)
      return showNotification("danger", t("orderPrice.allFieldsRequired"));

    setIsSubmitting(true);
    try {
      await axios.post(`${CONSTANTS.baseRoute}/locations-price`, newLocation);
      await fetchLocationPrices();
      setNewLocation({ region_id: "", city_id: "", price: "", express_price: "" });
      showNotification("success", t("orderPrice.priceAddedSuccess"));
    } catch (err) {
      showNotification("danger", t("orderPrice.priceAddError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveNewLocation = async (e) => {
    e.preventDefault();
    if (!newLocationData.region || !newLocationData.etrap)
      return showNotification("danger", t("orderPrice.regionEtrapRequired"));

    setIsSubmitting(true);
    try {
      await axios.post(`${CONSTANTS.baseRoute}/locations`, {
        region: newLocationData.region,
        region_en: newLocationData.region_en,
        region_ru: newLocationData.region_ru,
        city: newLocationData.etrap,
        city_en: newLocationData.etrap_en,
        city_ru: newLocationData.etrap_ru,
      });
      await fetchLocations();
      showNotification("success", t("orderPrice.locationAddedSuccess"));
      setShowLocationModal(false);
    } catch (err) {
      showNotification("danger", t("orderPrice.locationAddError"));
    } finally {
      setIsSubmitting(false);
    }
  };

const handleSaveEditLocation = async (e) => {
  e.preventDefault();
  if (!editingLocationData.region || !editingLocationData.etrap)
    return showNotification("danger", t("orderPrice.regionEtrapRequired"));

  setIsSubmitting(true);
  try {
    const payload = {
      welayat: editingLocationData.region,
      welayat_en: editingLocationData.region_en,
      welayat_ru: editingLocationData.region_ru,
      etrap: editingLocationData.etrap,         
      etrap_en: editingLocationData.etrap_en,   
      etrap_ru: editingLocationData.etrap_ru    
    };

    console.log("Backend'e giden tam veri:", payload);

    await axios.put(
      `${CONSTANTS.baseRoute}/locations/${editingLocationData.id}`,
      payload
    );

    await fetchLocations();
    showNotification("success", t("orderPrice.locationUpdatedSuccess"));
    setShowEditLocationModal(false);
  } catch (err) {
    console.error("Update hatası:", err);
    showNotification("danger", t("orderPrice.locationUpdateError"));
  } finally {
    setIsSubmitting(false);
  }
};
  // ── Ýeri pozmak ──
  const handleDeleteLocationFull = async (id) => {
    if (!window.confirm(t("orderPrice.confirmDeleteLocation"))) return;

    try {
      await axios.delete(`${CONSTANTS.baseRoute}/locations/${id}`);
      await fetchLocations();
      showNotification("success", t("orderPrice.locationDeletedSuccess"));
    } catch (err) {
      showNotification("danger", t("orderPrice.locationDeleteError"));
    }
  };

  // ── Baha pozmak ──
  const handleDeleteLocationPrice = async (id) => {
    if (!hasPermission(26)) return showNotification("danger", t("orderPrice.noPermissionDelete"));
    if (!window.confirm(t("orderPrice.confirmDeletePrice"))) return;

    try {
      await axios.delete(`${CONSTANTS.baseRoute}/locations-price/${id}`);
      setLocationPrices((prev) => prev.filter((loc) => loc.id !== id));
      showNotification("success", t("orderPrice.priceDeletedSuccess"));
    } catch (err) {
      showNotification("danger", t("orderPrice.priceDeleteError"));
    }
  };

  // ── Baha üýtgetmek modalyny açmak ──
  const handleEditLocationPrice = (loc) => {
    if (!hasPermission(25)) return showNotification("danger", t("orderPrice.noPermissionEdit"));

    setEditingLocationId(loc.id);
    setModalLocationData({
      region_id: loc.region_tm,
      city_id: loc.city_tm,
      price: loc.price,
      express_price: loc.express_price ?? "",
    });
    setShowModal(true);
  };

  // ── Modal içindäki baha saklamak ──
  const handleSaveModalChanges = async (e) => {
    e.preventDefault();
    if (!hasPermission(editingLocationId ? 25 : 24))
      return showNotification("danger", t("orderPrice.noPermissionAction"));

    if (!modalLocationData.region_id || !modalLocationData.city_id || !modalLocationData.price)
      return showNotification("danger", t("orderPrice.allFieldsRequired"));

    setIsSubmitting(true);
    try {
      const payload = {
        region_id: modalLocationData.region_id,
        city_id: modalLocationData.city_id,
        price: modalLocationData.price,
        express_price: modalLocationData.express_price,
      };

      if (editingLocationId) {
        await axios.put(
          `${CONSTANTS.baseRoute}/locations-price/${editingLocationId}`,
          payload
        );
        showNotification("success", t("orderPrice.priceUpdatedSuccess"));
      } else {
        await axios.post(`${CONSTANTS.baseRoute}/locations-price`, payload);
        showNotification("success", t("orderPrice.priceAddedSuccess"));
      }

      await fetchLocationPrices();
      handleCloseModal();
    } catch (err) {
      showNotification("danger", t("orderPrice.priceSaveError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLocationId(null);
    setModalLocationData({ region_id: "", city_id: "", price: "", express_price: "" });
  };

  // Select üçin dinamik filtrlenen welaýat we etraplar
  const regionOptions = [...new Set(locations.map((l) => l.region))].map((r) => ({
    value: r,
    label: r,
  }));

  const getCityOptions = (region) =>
    region
      ? locations
          .filter((l) => l.region === region)
          .map((l) => ({ value: l.city, label: l.city }))
      : [];

  return (
    <div className="admin-main ps-2 pe-3 py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex gap-2 align-items-center">
          <button className="btn admin-icon-btn" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn admin-icon-btn" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h4 className="admin-page-title">{t("orderPrice.title")}</h4>
        </div>

        <button
          className="btn btn-outline-primary d-flex align-items-center gap-2 shadow-sm"
          onClick={() => setShowLocationModal(true)}
        >
          <i className="bi bi-plus-circle"></i>
          {t("orderPrice.addNewLocation")}
        </button>
      </div>

      {/* Message / Alert */}
      {message.text && (
        <div className={`alert alert-${message.type} shadow-sm mb-4 alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ type: "", text: "" })}></button>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4 border-bottom-0" id="locationTabs" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link px-4 py-3 ${activeTab === "prices" ? "active" : ""}`}
            onClick={() => setActiveTab("prices")}
          >
            <i className="bi bi-currency-dollar me-2"></i>
            {t("orderPrice.tabPrices")}
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link px-4 py-3 ${activeTab === "locations" ? "active" : ""}`}
            onClick={() => setActiveTab("locations")}
          >
            <i className="bi bi-geo-alt me-2"></i>
            {t("orderPrice.tabLocations")}
          </button>
        </li>
      </ul>

      {/* ── Prices Tab ── */}
      {activeTab === "prices" && (
        <div className="row g-4">
          <div className={hasPermission(24) ? "col-lg-7" : "col-12"}>
            <div className="admin-card overflow-hidden">
              <div
                className="admin-card-header d-flex justify-content-between align-items-center"
              >
                <span><i className="bi bi-list-ul me-2"></i>{t("orderPrice.existingPrices")}</span>
                <span className="badge bg-light text-dark fs-6">{locationPrices.length}</span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0 text-center align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>{t("orderPrice.location")}</th>
                        <th>{t("orderPrice.price")}</th>
                        <th>{t("orderPrice.expressPrice")}</th>
                        <th className="text-end pe-4">{t("orderPrice.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locationPrices.length > 0 ? (
                        locationPrices.map((loc) => (
                          <tr key={loc.id}>
                            <td className="fw-medium">
                              <i className="bi bi-geo-fill text-warning me-2"></i>
                              {loc.region_tm} / {loc.city_tm}
                            </td>
                            <td>
                              <span className="badge bg-success fs-6 px-3 py-2">
                                {loc.price} TMT
                              </span>
                            </td>
                            <td>
                              {loc.express_price ? (
                                <span className="badge bg-warning text-dark fs-6 px-3 py-2">
                                  <i className="bi bi-lightning-charge-fill me-1"></i>
                                  {loc.express_price} TMT
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td className="text-end pe-4">
                              {hasPermission(25) && (
                                <button
                                  onClick={() => handleEditLocationPrice(loc)}
                                  className="btn btn-sm btn-outline-primary me-2"
                                  title={t("common.edit")}
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                              )}
                              {hasPermission(26) && (
                                <button
                                  onClick={() => handleDeleteLocationPrice(loc.id)}
                                  className="btn btn-sm btn-outline-danger"
                                  title={t("common.delete")}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="py-5 text-muted">
                            <i className="bi bi-inbox display-4 mb-3 d-block text-muted"></i>
                            {t("orderPrice.noPricesFound")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          {hasPermission(24) && (
            <div className="col-lg-5">
              <div className="admin-card">
                <div
                  className="admin-card-header text-center"
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  {t("orderPrice.addNewPrice")}
                </div>
                <div className="card-body p-4">
                  <form onSubmit={handleAddLocation}>
                    <div className="mb-2">
                      <label className="form-label fw-bold">{t("orderPrice.region")}</label>
                      <Select
                        options={regionOptions}
                        value={regionOptions.find(opt => opt.value === newLocation.region_id) || null}
                        onChange={(opt) => setNewLocation(prev => ({
                          ...prev,
                          region_id: opt?.value || "",
                          city_id: "",
                        }))}
                        placeholder={t("orderPrice.selectRegion")}
                        isClearable
                        isSearchable
                        styles={customSelectStyles}
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label fw-bold">{t("orderPrice.city")}</label>
                      <Select
                        options={getCityOptions(newLocation.region_id)}
                        value={getCityOptions(newLocation.region_id).find(opt => opt.value === newLocation.city_id) || null}
                        onChange={(opt) => setNewLocation(prev => ({
                          ...prev,
                          city_id: opt?.value || "",
                        }))}
                        placeholder={t("orderPrice.selectCity")}
                        isClearable
                        isSearchable
                        isDisabled={!newLocation.region_id}
                        styles={customSelectStyles}
                      />
                    </div>

                    <div className="mb-2">
                      <label className="form-label fw-bold">{t("orderPrice.priceTMT")}</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder=""
                        value={newLocation.price}
                        onChange={(e) => setNewLocation(prev => ({ ...prev, price: e.target.value }))}
                        min="0"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-bold">{t("orderPrice.expressPriceTMT")}</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder={t("orderPrice.expressPriceHint")}
                        value={newLocation.express_price}
                        onChange={(e) => setNewLocation(prev => ({ ...prev, express_price: e.target.value }))}
                        min="0"
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-success w-100 py-2 fw-bold shadow-sm"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          {t("common.saving")}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-save me-2"></i>
                          {t("common.save")}
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "locations" && (
        <div className="admin-card overflow-hidden">
          <div
            className="admin-card-header d-flex justify-content-between align-items-center"
          >
            <span><i className="bi bi-geo-alt-fill me-2"></i>{t("orderPrice.allLocations")}</span>
            <span className="badge bg-light text-dark fs-6">{locations.length}</span>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0 text-center align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t("orderPrice.regionTM")}</th>
                    <th>{t("orderPrice.regionRU")}</th>
                    <th>{t("orderPrice.cityTM")}</th>
                    <th>{t("orderPrice.cityRU")}</th>
                    <th className="text-end pe-4">{t("orderPrice.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.length > 0 ? (
                    locations.map((loc) => (
                      <tr key={loc.id}>
                        <td className="fw-medium">{loc.region}</td>
                        <td>{loc.region_ru || "—"}</td>
                        <td className="fw-medium">{loc.city}</td>
                        <td>{loc.city_ru || "—"}</td>
                        <td className="text-end pe-4">
                          <button
                            onClick={() => {
                              setEditingLocationData({
                                id: loc.id,
                                region: loc.region,
                                region_en: loc.region_en || "",
                                region_ru: loc.region_ru || "",
                                etrap: loc.city,
                                etrap_en: loc.city_en || "",
                                etrap_ru: loc.city_ru || "",
                              });
                              setShowEditLocationModal(true);
                            }}
                            className="btn btn-sm btn-outline-primary me-2"
                            title={t("common.edit")}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteLocationFull(loc.id)}
                            className="btn btn-sm btn-outline-danger"
                            title={t("common.delete")}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="py-5 text-muted">
                        <i className="bi bi-geo display-4 mb-3 d-block text-muted"></i>
                        {t("orderPrice.noLocationsFound")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content rounded-3 shadow-xl border-0">
              <div className="admin-card-header d-flex justify-content-between">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-pencil me-2"></i>
                  {t("orderPrice.editPrice")}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleSaveModalChanges}>
                  <div className="mb-4">
                    <label className="form-label fw-bold">{t("orderPrice.region")}</label>
                    <Select
                      options={regionOptions}
                      value={regionOptions.find(opt => opt.value === modalLocationData.region_id) || null}
                      onChange={(opt) => setModalLocationData(prev => ({
                        ...prev,
                        region_id: opt?.value || "",
                        city_id: "",
                      }))}
                      placeholder={t("orderPrice.selectRegion")}
                      isClearable
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">{t("orderPrice.city")}</label>
                    <Select
                      options={getCityOptions(modalLocationData.region_id)}
                      value={getCityOptions(modalLocationData.region_id).find(opt => opt.value === modalLocationData.city_id) || null}
                      onChange={(opt) => setModalLocationData(prev => ({
                        ...prev,
                        city_id: opt?.value || "",
                      }))}
                      placeholder={t("orderPrice.selectCity")}
                      isDisabled={!modalLocationData.region_id}
                      isClearable
                      styles={customSelectStyles}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">{t("orderPrice.priceTMT")}</label>
                    <input
                      type="number"
                      className="form-control shadow-sm"
                      value={modalLocationData.price}
                      onChange={(e) => setModalLocationData(prev => ({ ...prev, price: e.target.value }))}
                      min="0"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">{t("orderPrice.expressPriceTMT")}</label>
                    <input
                      type="number"
                      className="form-control shadow-sm"
                      placeholder={t("orderPrice.expressPriceHint")}
                      value={modalLocationData.express_price}
                      onChange={(e) => setModalLocationData(prev => ({ ...prev, express_price: e.target.value }))}
                      min="0"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-success w-100 py-2 fw-bold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.saving")}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        {t("common.save")}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLocationModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content rounded-3 shadow-xl border-0">
              <div className="admin-card-header d-flex justify-content-between">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-plus-circle me-2"></i>
                  {t("orderPrice.addNewLocation")}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowLocationModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleSaveNewLocation}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">{t("orderPrice.regionTM")}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newLocationData.region}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, region: e.target.value }))}
                        placeholder={t("orderPrice.enterRegionTM")}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">{t("orderPrice.regionRU")}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newLocationData.region_ru}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, region_ru: e.target.value }))}
                        placeholder={t("orderPrice.enterRegionRU")}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">{t("orderPrice.cityTM")}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newLocationData.etrap}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, etrap: e.target.value }))}
                        placeholder={t("orderPrice.enterCityTM")}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">{t("orderPrice.cityRU")}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newLocationData.etrap_ru}
                        onChange={(e) => setNewLocationData(prev => ({ ...prev, etrap_ru: e.target.value }))}
                        placeholder={t("orderPrice.enterCityRU")}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-success w-100 mt-4 py-2 fw-bold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.saving")}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        {t("common.add")}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Location Modal ── */}
      {showEditLocationModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content rounded-3 shadow-xl border-0">
              <div className="admin-card-header">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-pencil me-2"></i>
                  {t("orderPrice.editLocation")}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowEditLocationModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <form onSubmit={handleSaveEditLocation}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">{t("orderPrice.regionTM")}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingLocationData.region}
                        onChange={(e) => setEditingLocationData(prev => ({ ...prev, region: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">{t("orderPrice.regionRU")}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingLocationData.region_ru}
                        onChange={(e) => setEditingLocationData(prev => ({ ...prev, region_ru: e.target.value }))}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-bold">{t("orderPrice.cityTM")}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingLocationData.etrap}
                        onChange={(e) => setEditingLocationData(prev => ({ ...prev, etrap: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">{t("orderPrice.cityRU")}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingLocationData.etrap_ru}
                        onChange={(e) => setEditingLocationData(prev => ({ ...prev, etrap_ru: e.target.value }))}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-success w-100 mt-4 py-2 fw-bold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.saving")}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        {t("common.save")}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        .nav-tabs .nav-link {
          border: none;
          border-bottom: 3px solid transparent;
          color: #6c757d;
          font-weight: 500;
          transition: all 0.3s;
        }
        .nav-tabs .nav-link.active {
          color: var(--text-color);
          border-bottom: 3px solid var(--text-color);
          background: transparent;
        }
        .nav-tabs .nav-link:hover {
          color: var(--text-color);
        }
      `}</style>
    </div>
  );
}